import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ClassMembership } from '../class-membership.js';
import { ClassRoom } from '../classroom.js';
import { Exam } from '../exam.js';
import {
  EXAM_RESULT_DETAIL_LEVELS,
  ExamResultRelease,
  ReleasedTaskResult,
  type ExamResultDetailLevel,
} from '../exam-result-release.js';
import { Student } from '../student.js';
import { Submission } from '../submission.js';
import { Teacher } from '../teacher.js';
import { resolveResultsBlockedReason } from '../utils/exam-results-readiness.js';
import { findRosterMatches } from '../utils/student-name-match.js';
import { createSubmissionTaskRows, createTeacherSummary } from './generate-grading-reports.js';

/** One submission and who the roster says it might belong to. A PROPOSAL — nothing is bound yet. */
@Entity()
export class DistributionCandidateDTO extends AdornisEntity {
  static override _class = 'DistributionCandidateDTO';

  /** These arrive as an ARRAY, and the client cache refuses to merge entity arrays whose rows it cannot
   * tell apart. `AdornisEntity` has no `_id` of its own, so identity is stated here — one row per
   * submission, which is exactly what `submissionId` is. */
  static override resolveID(data: any) {
    return data?.submissionId;
  }

  @Field(() => String)
  submissionId!: string;

  /** The OCR'd name as it stands on the sheet, so the teacher can see what was matched against. */
  @Field(() => String)
  studentName: Maybe<string>;

  /** 'matched' (exactly one roster hit) | 'ambiguous' (several) | 'unmatched' (none, or a placeholder). */
  @Field(() => String)
  status!: string;

  /** The single hit when `matched`; null otherwise. A suggestion the teacher confirms, never a binding. */
  @Field(() => String)
  proposedStudentID: Maybe<string>;

  /** Every roster hit — more than one is exactly why `ambiguous` must not be resolved automatically. */
  @Field(() => [String])
  candidateStudentIDs: Maybe<string[]>;

  /** Already bound by an earlier release, so the teacher can see what re-releasing would keep. */
  @Field(() => String)
  boundStudentID: Maybe<string>;
}

@Entity()
export class DistributionProposalDTO extends AdornisEntity {
  static override _class = 'DistributionProposalDTO';

  /** Null when the exam may be released; otherwise why not — see `ResultsBlockedReason`. */
  @Field(() => String)
  blockedReason: Maybe<string>;

  @Field(() => [DistributionCandidateDTO])
  candidates: Maybe<DistributionCandidateDTO[]>;

  /** Approved class members with no submission proposed for them — absent, or handed in under another name. */
  @Field(() => [String])
  unmatchedStudentIDs: Maybe<string[]>;

  /** ISO timestamp of the last release, so the UI can say "already distributed". */
  @Field(() => String)
  lastReleasedAt: Maybe<string>;

  /** The detail level the last release actually used, so re-opening the dialog shows what is currently
   * live rather than just that something is. Null when nothing has been released yet. */
  @Field(() => String)
  lastDetailLevel: Maybe<string>;

  /** Whether the last release attached submission files — same reasoning as `lastDetailLevel`: without
   * this, re-opening the dialog would default the checkbox to unchecked and a re-release would silently
   * drop files a teacher had deliberately included before. */
  @Field(() => Boolean)
  lastIncludeSubmissionFiles: Maybe<boolean>;
}

/** Everything the release path needs, resolved once and authorized. */
async function loadExamContext(examId: string) {
  const trimmedExamId = typeof examId === 'string' ? examId.trim() : '';
  if (!trimmedExamId) throw new Error('Klausur nicht gefunden');

  const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
  if (!currentUser?._id) throw new Error('Nicht autorisiert: Kein User eingeloggt');

  const teacherCollection = await getCollection(Teacher);
  const teacher = await teacherCollection.findOne({ userID: currentUser._id });
  if (!teacher) throw new Error('Nicht autorisiert: Kein Lehrerprofil gefunden');

  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: trimmedExamId } as any);
  if (!exam) throw new Error('Klausur nicht gefunden');
  // `publishFilter` already scopes this read to the caller, but the check is stated rather than implied:
  // this is the authorization for publishing someone's grades, not a convenience.
  if ((exam as any).userID !== currentUser._id) throw new Error('Nicht autorisiert: Diese Klausur gehört dir nicht');

  const classRoomID = String((exam as any).classRoomID ?? '').trim();
  if (!classRoomID) throw new Error('Diese Klausur ist keiner Klasse zugeordnet');

  const classroom = await (await getCollection(ClassRoom)).findOne({ _id: classRoomID });
  if (!classroom) throw new Error('Klasse nicht gefunden');
  if (classroom.teacherID !== teacher._id) throw new Error('Nicht autorisiert: Diese Klasse gehört dir nicht');

  const membershipCollection = await getCollection(ClassMembership);
  const memberships = await membershipCollection.find({ classRoomID, status: 'approved' }).toArray();
  const studentCollection = await getCollection(Student);
  const roster = await studentCollection
    .find({ _id: { $in: memberships.map((m: any) => m.studentID) } } as any)
    .toArray();

  const submissions = await (await getCollection(Submission))
    .find({ examId: trimmedExamId })
    .sort({ submittedAt: 1, _id: 1 })
    .toArray();

  return { examId: trimmedExamId, exam, teacher, classroom, classRoomID, roster, submissions };
}

/**
 * What distributing this exam WOULD do — the mapping for a teacher to review. Reads only; no student can
 * see anything as a result of calling this.
 */
export const proposeExamResultDistributionQuery = registerQuery({
  operationName: 'proposeExamResultDistributionQuery',
  type: () => DistributionProposalDTO,
  params: [{ name: 'examId', type: () => String }],

  resolve:
    (examId: string) =>
    async (_selection: BaseQLSelectionSet<DistributionProposalDTO>): Promise<DistributionProposalDTO> => {
      const ctx = await loadExamContext(examId);

      const blockedReason = await resolveResultsBlockedReason(
        ctx.exam as any,
        ctx.examId,
        (ctx.exam as any).examState,
        ctx.submissions,
      );

      const releaseCollection = await getCollection(ExamResultRelease);
      const releases = await releaseCollection.find({ examId: ctx.examId }).toArray();

      const proposedIds = new Set<string>();
      const candidates = ctx.submissions.map((submission: any) => {
        const matches = findRosterMatches(submission?.studentName, ctx.roster as any[]);
        const bound = String(submission?.studentID ?? '').trim();

        // A binding confirmed by an earlier release outranks a fresh name guess: the teacher already
        // decided this one, and re-running the proposal must not quietly undo that.
        const status = bound
          ? 'matched'
          : matches.length === 1
            ? 'matched'
            : matches.length > 1
              ? 'ambiguous'
              : 'unmatched';
        const proposedStudentID = bound || (matches.length === 1 ? String(matches[0]!._id) : null);
        if (proposedStudentID) proposedIds.add(proposedStudentID);

        return new DistributionCandidateDTO({
          _id: String(submission._id),
          submissionId: String(submission._id),
          studentName: submission?.studentName ?? null,
          status,
          proposedStudentID,
          candidateStudentIDs: matches.map(m => String(m._id)),
          boundStudentID: bound || null,
        });
      });

      return new DistributionProposalDTO({
        blockedReason,
        candidates,
        unmatchedStudentIDs: (ctx.roster as any[]).map(s => String(s._id)).filter(id => !proposedIds.has(id)),
        lastReleasedAt:
          releases.length > 0
            ? (releases
                .map((r: any) => String(r.releasedAt))
                .sort()
                .slice(-1)[0] ?? null)
            : null,
        // Every row from the same release call shares one `releasedAt`/`detailLevel`/file-attachment choice
        // (the loop in `releaseExamResultsMutation` writes all three identically per submission), so any
        // row is representative.
        lastDetailLevel: releases[0] ? String((releases[0] as any).detailLevel ?? '') || null : null,
        lastIncludeSubmissionFiles: releases[0]
          ? Boolean((releases[0] as any).attachedImageIds?.length || (releases[0] as any).attachedPdfId)
          : null,
      });
    },
});

/**
 * Binds each submission to the student the teacher confirmed, and publishes a frozen snapshot of their
 * result. This is the only place a student's grade becomes visible to them.
 *
 * `assignments` is the teacher's decision, one entry per submission: a `studentID` to release to, or null
 * to exclude that submission. Every submission must appear — a missing one is treated as unresolved and the
 * whole release is refused, because a silently skipped submission is a student who never gets their result
 * and nobody noticing.
 */
export const releaseExamResultsMutation = registerMutation({
  operationName: 'releaseExamResultsMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'submissionIds', type: () => [String] },
    /** Parallel to `submissionIds`; an empty string means "do not distribute this one". */
    { name: 'studentIDs', type: () => [String] },
    { name: 'detailLevel', type: () => String },
    /** "Eigene Abgabe (PDF/Seiten) mitverteilen" — attach the student's own scanned pages/PDF alongside
     * the grading result. Independent of `detailLevel`: even a `grade_only` release can carry it. */
    { name: 'includeSubmissionFiles', type: () => Boolean },
  ],

  resolve:
    (examId: string, submissionIds: string[], studentIDs: string[], detailLevel: string, includeSubmissionFiles: boolean) =>
    async (): Promise<boolean> => {
      const ctx = await loadExamContext(examId);

      if (!Array.isArray(submissionIds) || !Array.isArray(studentIDs) || submissionIds.length !== studentIDs.length) {
        throw new Error('Ungültige Zuordnung');
      }

      // Rejected rather than defaulted: a value that reached here without going through the UI's dropdown is
      // not something to guess at silently, the same posture the "every submission accounted for" check below
      // already takes.
      if (!EXAM_RESULT_DETAIL_LEVELS.includes(detailLevel as ExamResultDetailLevel)) {
        throw new Error('Ungültiger Detailgrad');
      }
      const level = detailLevel as ExamResultDetailLevel;

      const blockedReason = await resolveResultsBlockedReason(
        ctx.exam as any,
        ctx.examId,
        (ctx.exam as any).examState,
        ctx.submissions,
      );
      // Re-checked here and not merely in the UI: the gate is what keeps half-graded or unreviewed work from
      // reaching students, so it has to hold against any caller, not just the button that usually calls this.
      if (blockedReason) throw new Error(`Ergebnisse können noch nicht verteilt werden (${blockedReason}).`);

      const assignments = new Map<string, string>();
      submissionIds.forEach((submissionId, index) => {
        assignments.set(String(submissionId), String(studentIDs[index] ?? '').trim());
      });

      // Every submission accounted for, or nothing goes out.
      for (const submission of ctx.submissions as any[]) {
        if (!assignments.has(String(submission._id))) {
          throw new Error('Es sind noch nicht alle Abgaben zugeordnet.');
        }
      }

      const rosterIds = new Set((ctx.roster as any[]).map(s => String(s._id)));
      const seenStudents = new Set<string>();
      for (const [, studentID] of assignments) {
        if (!studentID) continue;
        // Releasing to someone who is not an approved member of this class would hand a grade to an outsider.
        if (!rosterIds.has(studentID)) throw new Error('Eine Abgabe wurde jemandem außerhalb der Klasse zugeordnet.');
        // Two papers for one student means at least one of them is somebody else's.
        if (seenStudents.has(studentID)) throw new Error('Einem Schüler wurden mehrere Abgaben zugeordnet.');
        seenStudents.add(studentID);
      }

      const releasedAt = new Date().toISOString();
      const rawSubmissions = await getRawCollection(Submission._collectionName);
      const releaseCollection = await getCollection(ExamResultRelease);
      const rawReleases = await getRawCollection(ExamResultRelease._collectionName);

      // A re-release supersedes the previous snapshot wholesale, so stale rows for students who are no longer
      // assigned do not linger and keep showing a result that was withdrawn.
      await rawReleases.deleteMany({ examId: ctx.examId });

      let released = 0;
      for (const submission of ctx.submissions as any[]) {
        const studentID = assignments.get(String(submission._id)) ?? '';

        await rawSubmissions.updateOne({ _id: submission._id }, { $set: { studentID: studentID || null } });
        if (!studentID) continue;

        const summary = createTeacherSummary(ctx.exam as any, [submission]).studentSummaries[0];

        // The level is enforced HERE, not by the client hiding fields it received: `grade_only` never even
        // walks the task tree, `points_per_task`/`points_and_answer` walk it but leave whatever sits above
        // their line null. A release's `tasks` array genuinely does not contain more than what its
        // `detailLevel` promises — the two flags below are the only place that promise is decided.
        const taskRows = level === 'grade_only' ? [] : createSubmissionTaskRows(ctx.exam as any, submission);
        const includeAnswer = level === 'points_and_answer' || level === 'full';
        const includeFeedback = level === 'full';

        // Same fail-closed rule as the detail level: unchecked means these fields are never populated,
        // not merely left unrendered. A scanned submission has no `submissionPdfId` (only online ones do —
        // see the field's own doc comment in submission.ts), so `attachedPdfId` legitimately stays null
        // there even when the teacher asked for files.
        const attachedImageIds = includeSubmissionFiles ? (submission?.imageIds ?? null) : null;
        const attachedPdfId = includeSubmissionFiles ? (submission?.submissionPdfId ?? null) : null;

        // Built through the entity and `toObject()` rather than as a literal, like every other insert in
        // db/api — that is what stamps `_id`/`_class` and applies field defaults, without which the document
        // does not hydrate back into an entity on read.
        const release = new ExamResultRelease({
          examId: ctx.examId,
          classRoomID: ctx.classRoomID,
          studentID,
          submissionId: String(submission._id),
          examTitle: (ctx.exam as any).title ?? null,
          examSubject: (ctx.exam as any).subject ?? null,
          grade: summary?.grade ?? null,
          percentage: summary?.percentage ?? null,
          totalPoints: summary?.totalPoints ?? null,
          totalMaxPoints: summary?.totalMaxPoints ?? null,
          // `gradingScheme` and `masterSolution` are dropped on purpose — teacher-internal, see the DTO docs.
          tasks: taskRows.map(
            row =>
              new ReleasedTaskResult({
                label: row.label,
                description: row.description || null,
                studentAnswer: includeAnswer ? row.studentAnswer || null : null,
                points: row.points,
                maxPoints: row.maxPoints,
                feedback: includeFeedback ? row.explanation || null : null,
              }),
          ),
          detailLevel: level,
          attachedImageIds,
          attachedPdfId,
          releasedAt,
          releasedBy: ctx.teacher._id,
        } as any);

        await releaseCollection.insertOne(release.toObject() as any);
        released += 1;
      }

      logger.info(
        { examId: ctx.examId, teacherID: ctx.teacher._id, released, total: ctx.submissions.length },
        'Exam results released to students',
      );

      return true;
    },
});

/** Withdraws a distribution: the snapshots go, students lose access immediately. The `Submission.studentID`
 * bindings stay — whose paper it is does not stop being true because the result was withdrawn. */
export const revokeExamResultsMutation = registerMutation({
  operationName: 'revokeExamResultsMutation',
  type: () => Boolean,
  params: [{ name: 'examId', type: () => String }],

  resolve: (examId: string) => async (): Promise<boolean> => {
    const ctx = await loadExamContext(examId);

    const rawReleases = await getRawCollection(ExamResultRelease._collectionName);
    const result = await rawReleases.deleteMany({ examId: ctx.examId });

    logger.info(
      { examId: ctx.examId, teacherID: ctx.teacher._id, revoked: result?.deletedCount ?? 0 },
      'Exam results revoked',
    );

    return true;
  },
});
